import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../components/core/Button/Button";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import {
  SUB_COMPETENCE_LABEL,
  DELETE_TECH_SKILL_CONFIRM,
  DELETE_TECH_SKILL_LABEL,
  DESCRIPTION_LABEL,
  EMPTY_STATE_TECH_SKILLS_MESSAGE,
  EMPTY_TECH_SKILLS_MESSAGE,
  SEARCH_LABEL,
  SCOPE_LABEL,
  CORE_COMPETENCE_LABEL,
  TECH_SKILLS_LABEL,
  TECH_SKILL_LABEL,
} from "../../constants/Dictionary";
import { StyledTechSkills } from "./TechSkills.styled";
import techSkillsImage from "../../images/skills.png";
import { ReactComponent as AddIcon } from "../../icons/add.svg";
import { TechSkillModal } from "./TechSkillModal/TechSkillModal";
import { DoubleStructure } from "../../components/core/DoubleStructure/DoubleStructure";
import { AlignmentEnum, FontWeightEnum, PageNames } from "../../types/AppTypes";
import { BodyTyp } from "../../components/Typography/Typography";
import SearchInput from "../../components/core/SearchInput/SearchInput";
import { Link, LinkSizeEnum } from "../../components/core/Link/Link";
import { PopupConfirm } from "../../components/PopupConfirm/PopupConfirm";
import { TechSkill } from "../../types/api/techSkills.type";
import {
  PermissionsEnum,
  TECH_SKILLS_PAGE_SIZE,
} from "../../constants/AppConstants";
import { useDebounce } from "use-debounce";
import { roleHasPermission } from "../../utils/roleAuth.utils";
import { useAppSelector } from "../../hooks/store.hook";
import { Table } from "../../components/core/Table/Table";
import {
  TextBox,
  TextBoxGrid,
} from "../../components/TextBoxGrid/TextBoxGrid.component";
import { useBreadCrumb } from "../../hooks/useBreadCrumbs.hooks";
import { techSkillsTableColumns } from "../../constants/TableColumns";
import { Loader } from "../../components/core/Loader/Loader";
import LoadingThumbnail from "../../components/LoadingThumbnail/LoadingThumbnail";
import { useDispatch } from "react-redux";
import {
  setPaginationTechSkills,
  setSearchTechSkills,
} from "../../stores/actions/techSkill.actions";
import { useDimensions } from "../../hooks/useDimensions.hook";
import {
  useDeleteTechSkill,
  useTechSkills,
} from "../../hooks/techSkills.hooks";

export const TechSkills = () => {
  const dispatch = useDispatch();

  const { profile } = useAppSelector((state) => state.user);
  const [openTechSkillModal, setOpenTechSkillModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<TechSkill | null>(null);
  const [hasData, setHasData] = useState<boolean | null>(null);

  const { searchTechSkills, paginationTechSkills } = useAppSelector(
    (state) => state.techSkill,
  );

  const [debounceSearchTechSkill] = useDebounce(searchTechSkills, 500);

  const { isMobile } = useDimensions();

  const hideTechSkillModal = () => {
    setSelectedRecord(null);
    setOpenTechSkillModal(false);
  };

  const hideDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedRecord(null);
  };

  const onEdit = (record: TechSkill) => {
    setSelectedRecord(record);
    setOpenTechSkillModal(true);
  };

  const onDelete = (record: TechSkill) => {
    setSelectedRecord(record);
    setOpenDeleteModal(true);
  };

  const { deleteTechSkill, isDeleting } = useDeleteTechSkill();

  const { data, isLoading, isFetching } = useTechSkills({
    paginationTechSkills,
    debounceSearchTechSkill,
  });

  useEffect(() => {
    if (!isLoading && data && hasData === null) {
      if (searchTechSkills.length > 0) {
        setHasData(true);
        return;
      }
      const newHasData = data.pagination.total > 0;
      setHasData(newHasData);
    }
  }, [data, hasData, isLoading]);

  const handleUpdateTechSkills = async (currentHasData: boolean | null) => {
    if (currentHasData === false) {
      setHasData(null);
    }
  };

  useBreadCrumb([
    {
      name: TECH_SKILLS_LABEL,
      pathName: PageNames.TECH_SKILLS,
    },
  ]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(setPaginationTechSkills(0));
  }, [debounceSearchTechSkill]);

  const expandedRow = (record: TechSkill): React.ReactElement | null => {
    return (
      <>
        {isMobile && (
          <TextBoxGrid
            numCol={2}
            items={[
              { label: SCOPE_LABEL, text: record.stream },
              { label: CORE_COMPETENCE_LABEL, text: record.software },
              { label: SUB_COMPETENCE_LABEL, text: record.component },
            ]}
          />
        )}
        <TextBox label={DESCRIPTION_LABEL} text={record.description} />
      </>
    );
  };

  const calcTableHeight = (): string => {
    let defaultHeight = 300;

    if (roleHasPermission(profile, PermissionsEnum.EDIT_TECH_FORMATION)) {
      defaultHeight += 44;
    }

    return `calc(100dvh - ${defaultHeight}px)`;
  };

  const handleDeleteTechSkill = async (id: number) => {
    await deleteTechSkill(id);

    hideDeleteModal();
  };

  return (
    <>
      <StyledTechSkills>
        {isLoading && hasData === null ? (
          <Loader loading={true}>
            <LoadingThumbnail marginTop={316} />
          </Loader>
        ) : null}
        {!isLoading && hasData === false ? (
          <div className="empty-state">
            <EmptyState
              label={EMPTY_STATE_TECH_SKILLS_MESSAGE}
              imageSrc={techSkillsImage}
            >
              {roleHasPermission(
                profile,
                PermissionsEnum.EDIT_TECH_FORMATION,
              ) && (
                <Button
                  label={TECH_SKILL_LABEL}
                  variant={ButtonVariants.SECONDARY}
                  size={ButtonSizes.REGULAR}
                  icon={<AddIcon />}
                  onClick={() => setOpenTechSkillModal(true)}
                />
              )}
            </EmptyState>
          </div>
        ) : null}
        {hasData ? (
          <>
            <div className="tech-skills-header-container">
              {roleHasPermission(
                profile,
                PermissionsEnum.EDIT_TECH_FORMATION,
              ) && (
                <Link
                  onClick={() => setOpenTechSkillModal(true)}
                  icon={<AddIcon />}
                  size={LinkSizeEnum.SMALL}
                >
                  {TECH_SKILL_LABEL}
                </Link>
              )}
              <DoubleStructure
                verticalAlignment={AlignmentEnum.CENTER}
                variant="primary"
              >
                <div>
                  {data && (
                    <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                      {data.pagination.total == 1
                        ? `${data.pagination.total} ${TECH_SKILL_LABEL}`
                        : `${data.pagination.total} ${TECH_SKILLS_LABEL}`}
                    </BodyTyp>
                  )}
                </div>
                <div className="search-container">
                  <SearchInput
                    value={searchTechSkills}
                    onChange={(value) => dispatch(setSearchTechSkills(value))}
                    label={`${SEARCH_LABEL}...`}
                    name={"search"}
                  />
                </div>
              </DoubleStructure>
            </div>
            <div className="tech-skills-content">
              <Table<TechSkill>
                loading={isFetching}
                rowKey="id"
                headers={techSkillsTableColumns(isMobile)}
                scrollY={calcTableHeight()}
                {...(roleHasPermission(
                  profile,
                  PermissionsEnum.EDIT_TECH_FORMATION,
                ) && {
                  onEdit: onEdit,
                  onDelete: onDelete,
                })}
                data={data?.data ?? []}
                expandable={{
                  expandedRowRender: expandedRow,
                }}
                pagination={{
                  current: paginationTechSkills + 1,
                  onChange: (pageNumber) =>
                    dispatch(setPaginationTechSkills(pageNumber - 1)),
                  total: data?.pagination.total ?? 0,
                  pageSize: TECH_SKILLS_PAGE_SIZE,
                }}
                emptyText={EMPTY_TECH_SKILLS_MESSAGE}
              />
            </div>
          </>
        ) : null}
      </StyledTechSkills>
      {openTechSkillModal && (
        <TechSkillModal
          closeModal={hideTechSkillModal}
          onSave={() => handleUpdateTechSkills(hasData)}
          {...(selectedRecord && {
            record: selectedRecord,
          })}
        />
      )}
      <PopupConfirm
        isOpen={openDeleteModal}
        onCancel={hideDeleteModal}
        onConfirm={
          selectedRecord
            ? () => handleDeleteTechSkill(selectedRecord.id)
            : () => {}
        }
        label={DELETE_TECH_SKILL_LABEL}
        isLoading={isDeleting}
        popupText={
          selectedRecord
            ? `${DELETE_TECH_SKILL_CONFIRM} ${selectedRecord.competence}?`
            : "error"
        }
      />
    </>
  );
};
